'use client';

import { Loader2, Plus, Trash2, FileDown } from 'lucide-react';
import { useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { postMultipartResourceUpload } from '@/lib/vertex-upload';

export interface ProjectDocumentDraft {
  id?: string;
  name: string;
  url: string;
  mimeType: string;
  size: number;
}

interface ProjectDocumentsEditorProps {
  documents: ProjectDocumentDraft[];
  /** Must accept functional updates (`prev => …`) so name edits never use a stale list and drop `url`. */
  onChange: Dispatch<SetStateAction<ProjectDocumentDraft[]>>;
  disabled?: boolean;
  uploadFolder?: string;
}

const MAX_DOCS = 5;
const MAX_NAME_LEN = 200;

export function ProjectDocumentsEditor({
  documents,
  onChange,
  disabled,
  uploadFolder = 'projects/documents',
}: ProjectDocumentsEditorProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  async function onFilesSelected(files: FileList | null) {
    if (!files?.length || disabled || isUploading) return;

    const room = MAX_DOCS - documents.length;
    if (room <= 0) {
      toast.error(`Maximum ${MAX_DOCS} documents allowed`);
      return;
    }

    const slice = Math.min(room, files.length);
    const toAdd: ProjectDocumentDraft[] = [];

    setIsUploading(true);
    try {
      for (let i = 0; i < slice; i++) {
        const file = files[i]!;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('filename', file.name);
        formData.append('folder', uploadFolder);
        const { url } = await postMultipartResourceUpload(formData);
        const name =
          file.name.length > MAX_NAME_LEN ? `${file.name.slice(0, MAX_NAME_LEN - 1)}…` : file.name;
        toAdd.push({
          url,
          name,
          mimeType: file.type || 'application/octet-stream',
          size: file.size,
        });
      }
      onChange((prev) => [...prev, ...toAdd]);
      toast.success(slice === 1 ? 'Document uploaded' : `${slice} documents uploaded`);
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setIsUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  function patchDocument(index: number, patch: Partial<ProjectDocumentDraft>) {
    onChange((prev) => prev.map((doc, i) => (i === index ? { ...doc, ...patch } : doc)));
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <Label className="text-base font-semibold">Downloadable documents</Label>
          <p className="text-sm text-muted-foreground mt-1">
            Up to {MAX_DOCS} files, any type. The title defaults to the file name — you can change
            it below.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || isUploading || documents.length >= MAX_DOCS}
          onClick={() => inputRef.current?.click()}
        >
          {isUploading ? (
            <>
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              Uploading…
            </>
          ) : (
            <>
              <Plus className="h-4 w-4 mr-1" />
              Add document
            </>
          )}
        </Button>
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        disabled={disabled || isUploading}
        onChange={(e) => onFilesSelected(e.target.files)}
      />

      {documents.length === 0 ? (
        <div className="border border-dashed rounded-lg p-6 text-center text-muted-foreground text-sm">
          No documents attached.
        </div>
      ) : (
        <ul className="space-y-3">
          {documents.map((d, idx) => (
            <li key={d.id || `${d.url}-${idx}`} className="rounded-lg border bg-card p-3">
              <div className="space-y-2">
                <div className="space-y-1.5">
                  <Label htmlFor={`project-doc-name-${idx}`} className="text-xs">
                    Document name
                  </Label>
                  <div className="flex items-center gap-2">
                    <FileDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                    <Input
                      id={`project-doc-name-${idx}`}
                      value={d.name}
                      onChange={(e) =>
                        patchDocument(idx, {
                          name: e.target.value.slice(0, MAX_NAME_LEN),
                        })
                      }
                      placeholder="Title shown to viewers"
                      disabled={disabled || isUploading}
                      maxLength={MAX_NAME_LEN}
                      className="min-w-0 flex-1"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="shrink-0 text-destructive"
                      disabled={disabled || isUploading}
                      aria-label="Remove document"
                      onClick={() => onChange((prev) => prev.filter((_, i) => i !== idx))}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground pl-6">
                  {d.mimeType}
                  {d.size > 0 ? ` · ${(d.size / 1024).toFixed(1)} KB` : ''}
                  {' · '}
                  <a
                    href={d.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-primary underline-offset-2 hover:underline"
                  >
                    Open file
                  </a>
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
